/**
 * Hostiles of the village family (vanilla Java, normal difficulty):
 *  - pillager: 24 HP, speed 0.35, crossbow (25-tick charge, arrow power 1.6,
 *    base damage 2 -> 4 on normal), keeps ~8 blocks away and strafes; hunts
 *    players, villagers and iron golems. Drops 0-2 arrows, 8.5% crossbow.
 *    A patrol captain wears the ominous banner (`captain`).
 *  - vindicator: 24 HP, speed 0.35, iron axe 13 damage (axes disable shields),
 *    same targets; arms crossed until it has a target.
 *  - zombie villager: a zombie (20 HP, 3 damage, burns in daylight) with the
 *    villager's head and robe; keeps the type / profession it had. Curing
 *    (weakness + golden apple) is not implemented — there are no potions.
 */

import { I } from '../../game/itemIds';
import { professionIndex, PROFESSIONS, type Profession } from '../../game/trading';
import { stackOf, type Stack } from '../../game/types';
import type { MobCtx } from '../mob';
import { playSound, roll, scheduleArrowHit, vh, VMob, type Target } from './ctx';

abstract class Raider extends VMob {
  protected target: Target = null;
  protected seeMem = 0;
  protected attackCd = 0;

  override onHurtBy(): void { this.retaliate = 200; this.target = 'player'; }
  onHurtByMob(m: VMob | { type: string }): void { if (m.type === 'iron_golem' || m.type === 'villager') this.target = m as VMob; }
  xpValue(): number { return 5; }

  /** Player within `range` (by sight, remembered 60 ticks), else the nearest villager / golem within 16. */
  protected pick(ctx: MobCtx, range: number): Target {
    const p = ctx.host.player;
    if (ctx.targetable()) {
      const d2 = (p.x - this.x) ** 2 + (p.y - this.y) ** 2 + (p.z - this.z) ** 2;
      if (d2 < range * range) {
        if (this.phase(ctx, 5) && (ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z))) this.seeMem = 60;
        if (this.retaliate > 0) this.seeMem = Math.max(this.seeMem, 1);
        if (this.seeMem > 0) { this.seeMem--; return 'player'; }
      }
    }
    if (this.target && this.target !== 'player' && this.targetAlive(this.target, ctx) && this.dist2(this.target, ctx) < 24 * 24) return this.target;
    if (this.phase(ctx, 10)) {
      const m = this.nearest(16, (o) => o.type === 'villager' || o.type === 'iron_golem');
      if (m && this.sees(m, ctx)) return m;
    }
    return null;
  }
}

export class Pillager extends Raider {
  captain = false;
  private charge = 0;
  private strafeT = 0;
  private strafeLeft = false;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'pillager', x, y, z, { width: 0.6, height: 1.95, eye: 1.62, health: 24, speed: 0.35 }, rnd);
    this.heldItem = I.CROSSBOW;
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    this.target = this.pick(ctx, 32);
    const t = this.target;
    if (!t) {
      this.charge = 0;
      this.stroll(ctx, 0.6, 3, false);
      this.lookAtNearPlayer(ctx);
      return;
    }
    const p = this.tx(t, ctx);
    const dx = p.x - this.x, dz = p.z - this.z;
    const d = Math.hypot(dx, dz) || 1;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    const sees = this.sees(t, ctx);
    // vanilla RangedCrossbowAttackGoal (radius 8): close in, back off, strafe
    if (!sees || d > 10) this.goToward(p.x, p.z, 1, false);
    else if (d < 5) this.goAway(p.x, p.z, 0.8);
    else {
      if (++this.strafeT > 40) { this.strafeT = 0; if (ctx.host.rnd() < 0.5) this.strafeLeft = !this.strafeLeft; }
      const sx = (this.strafeLeft ? 1 : -1) * -dz / d, sz = (this.strafeLeft ? 1 : -1) * dx / d;
      if (this.safeAhead(sx, sz, 3, false)) this.setMove(sx, sz, 0.5);
    }
    if (sees && d < 16) {
      this.aggressive = true;
      if (this.charge === 0) playSound('item.crossbow.loading_start', this);
      if (this.charge < 25) this.charge++;
      if (this.charge === 25 && this.attackCd <= 0) this.shoot(ctx, t);
    } else if (this.charge > 0) this.charge--;
  }

  private shoot(ctx: MobCtx, t: Exclude<Target, null>): void {
    const p = this.tx(t, ctx);
    const sx = this.x, sy = this.eyeY - 0.1, sz = this.z;
    const dx = p.x - sx, dy = p.y + p.h * 0.333 - sy, dz = p.z - sz;
    const h = Math.hypot(dx, dz);
    const diff = vh.difficulty();
    const inacc = 14 - (diff === 'easy' ? 1 : diff === 'normal' ? 2 : 3) * 4;
    ctx.shootArrow(sx, sy, sz, dx, dy + h * 0.2, dz, 1.6, 2, false, inacc, this.id);
    // arrows only collide with the player; a mob target takes the hit when it would land
    if (t !== 'player') scheduleArrowHit(this, t, 4, Math.hypot(dx, dy, dz) / 1.6);
    playSound('item.crossbow.shoot', this);
    this.charge = 0;
    this.attackCd = 20 + Math.floor(ctx.host.rnd() * 20);
    this.swing();
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    const out: Stack[] = [];
    const a = roll(rnd, 0, 2);
    if (a > 0) out.push(stackOf(I.ARROW, a));
    if (byPlayer && rnd() < 0.085) out.push({ id: I.CROSSBOW, count: 1, dmg: Math.floor(rnd() * 400) });
    return out;
  }

  override syncView(host: Parameters<VMob['syncView']>[0]): void {
    this.color = this.captain ? 1 : 0;
    super.syncView(host);
  }

  saveExtra(): unknown { return { c: this.captain ? 1 : 0, p: this.persistent ? 1 : 0 }; }
  loadExtra(d: unknown): void {
    const o = d as { c?: number; p?: number };
    if (!o || typeof o !== 'object') return;
    this.captain = o.c === 1; this.persistent = o.p === 1;
  }
}

export class Vindicator extends Raider {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'vindicator', x, y, z, { width: 0.6, height: 1.95, eye: 1.62, health: 24, speed: 0.35 }, rnd);
    this.heldItem = I.IRON_AXE;
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    this.target = this.pick(ctx, 24);
    const t = this.target;
    if (!t) { this.stroll(ctx, 0.6, 3, false); this.lookAtNearPlayer(ctx); return; }
    const p = this.tx(t, ctx);
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    this.goToward(p.x, p.z, 1, false);
    if (this.attackCd === 0 && this.inReach(t, ctx)) {
      this.attackCd = 20;
      this.swing();
      this.strike(t, ctx, 13, 0.4);
    }
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    const out: Stack[] = [];
    if (byPlayer && rnd() < 0.085) out.push({ id: I.IRON_AXE, count: 1, dmg: Math.floor(rnd() * 200) });
    const e = roll(rnd, 0, 1);
    if (e) out.push(stackOf(I.EMERALD, e));
    return out;
  }
}

export class ZombieVillager extends Raider {
  vtype = 0;
  profession: Profession = 'none';

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'zombie_villager', x, y, z, { width: 0.6, height: 1.95, eye: 1.74, health: 20, speed: 0.23 }, rnd);
  }

  protected ai(ctx: MobCtx): void {
    this.sunBurn(ctx);
    if (this.attackCd > 0) this.attackCd--;
    this.target = this.pick(ctx, 35);
    const t = this.target;
    if (!t) { this.stroll(ctx, 0.8, 4, false); this.lookAtNearPlayer(ctx); return; }
    const p = this.tx(t, ctx);
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    this.goToward(p.x, p.z, 1, false);
    if (this.attackCd === 0 && this.inReach(t, ctx)) {
      this.attackCd = 20;
      this.swing();
      this.strike(t, ctx, 3, 0.4);
    }
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    const out: Stack[] = [];
    const f = roll(rnd, 0, 2);
    if (f > 0) out.push(stackOf(I.ROTTEN_FLESH, f));
    if (byPlayer && rnd() < 1 / 40) out.push(stackOf(I.IRON_INGOT, 1));
    return out;
  }

  override syncView(host: Parameters<VMob['syncView']>[0]): void {
    this.color = (this.vtype & 7) | (professionIndex(this.profession) << 3);
    super.syncView(host);
  }

  saveExtra(): unknown { return { t: this.vtype, p: this.profession }; }
  loadExtra(d: unknown): void {
    const o = d as { t?: number; p?: string };
    if (!o || typeof o !== 'object') return;
    this.vtype = (o.t ?? 0) & 7;
    this.profession = (PROFESSIONS as readonly string[]).includes(o.p ?? '') ? o.p as Profession : 'none';
  }
}
